// Skills Gap tab, DA.pdf page 2.
//
// "Skills with Gaps" lists attempted BASE skills, filterable by outcome:
// correct, or one card per misconception type. The summary strip, the cards
// and the row dots all read from `countByOutcome` and `outcomeOf`, so they
// cannot drift apart. The backend still returns `priority` per row and
// `priority_counts`; only `chains_triggered` is consumed, and the rest stays
// so nothing else reading the endpoint breaks.
//
// The mockup's STRAND and SUBSTRAND columns are not rendered yet. They need a
// curriculum-team mapping that does not exist in the data model.
//
// "Mastery by Skill Area" now lives in BmdMasteryBreakdownTab.tsx, on the
// Mastery Breakdown tab, where it drills cluster -> topic -> subtopic.

import React from "react"
import { getStudentSkillsGap } from "../services/bmd-report.service"
import type {
  BmdReportKpi,
  BmdSkillsGapScope,
  BmdSkillGapRow,
  GetStudentSkillsGapResponse,
} from "../types/bmd-report.types"
import {
  abbreviateYearLevel,
  humanMisconception,
  parseYearNumber,
  plainText,
} from "../utils/formatters"

// ============================================================================
// Constants
// ============================================================================

const PRIORITY_FILTER_ALL = "__all__"

type SummaryStripProps = {
  studentName: string
  baseYearLabel: string
  topicCount: number
  attemptedCount: number
  totalQuestions: number
  correctCount: number
  accuracyPct: number
  incorrectCount: number
  // Counted from the same rows the cards use, so the two can never disagree.
  rows: BmdSkillGapRow[]
  chainsTriggered: number
}

const SummaryStrip: React.FC<SummaryStripProps> = ({
  studentName,
  baseYearLabel,
  topicCount,
  attemptedCount,
  correctCount,
  accuracyPct,
  incorrectCount,
  rows,
  chainsTriggered,
}) => {
  const outcomeCounts = countByOutcome(rows)
  const initials = studentName
    .split(/\s+/)
    .map((p) => p[0] || "")
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3 d-flex align-items-center flex-wrap gap-3">
      <div
        className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
        style={{
          width: 44,
          height: 44,
          backgroundColor: "#fde2e1",
          color: "#b42318",
        }}
      >
        {initials || "—"}
      </div>
      <div className="flex-grow-1">
        <div className="fw-bold font-size-15 mb-1">{studentName}</div>
        <div className="text-muted font-size-13">
          {baseYearLabel} (Base) · Across {topicCount} topic
          {topicCount === 1 ? "" : "s"} · Attempted {attemptedCount} · Correct{" "}
          {correctCount} ({accuracyPct}%) · Incorrect {incorrectCount}
        </div>
      </div>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        {OUTCOME_ORDER.map((key) => (
          <OutcomeBadge key={key} outcome={key} count={outcomeCounts[key]} />
        ))}
        <ChainsTriggeredBadge count={chainsTriggered} />
      </div>
    </div>
  )
}

const OutcomeBadge: React.FC<{
  outcome: OutcomeKey
  count: number
}> = ({ outcome, count }) => {
  const meta = OUTCOME_META[outcome]
  return (
    <div className="text-center">
      <div className="text-uppercase text-muted font-size-11 fw-semibold">
        {meta.label}
      </div>
      <div
        className="fw-bold font-size-18 mt-1 d-inline-flex align-items-center"
        style={{ color: meta.text }}
      >
        {count}
        <span
          className="rounded-circle ms-2 d-inline-block"
          style={{ width: 8, height: 8, backgroundColor: meta.dot }}
          aria-hidden="true"
        ></span>
      </div>
    </div>
  )
}

const ChainsTriggeredBadge: React.FC<{ count: number }> = ({ count }) => (
  <div className="text-center">
    <div className="text-uppercase text-muted font-size-11 fw-semibold">
      Chains Triggered
    </div>
    <div
      className="fw-bold font-size-18 mt-1 d-inline-flex align-items-center"
      style={{ color: "#1f2a44" }}
    >
      {count}
      <i
        className="fas fa-link ms-2 font-size-13 text-muted"
        aria-hidden="true"
      ></i>
    </div>
  </div>
)

// The cards the tutor filters by: one for correct answers, then one per
// misconception type, so the breakdown matches what the diagnostic recorded
// rather than a derived priority band.
//
// There is deliberately no "untagged" card. All 488 DQs that BMD serves carry
// a misconception on every wrong option, and the 577 untagged DQs are legacy
// content (ids 3501-4100) that BMD's selection rule never picks. If untagged
// answers do appear, they stay reachable under "All" and the card counts
// simply will not sum to the row count.
type OutcomeKey = "correct" | "FUNDAMENTAL" | "RECENTLY_LEARNED" | "LACK_OF_DEPTH"

const OUTCOME_META: Record<
  OutcomeKey,
  { label: string; bg: string; text: string; dot: string }
> = {
  correct: {
    label: "Correct",
    bg: "#d1fae5",
    text: "#047857",
    dot: "#10b981",
  },
  FUNDAMENTAL: {
    label: "Fundamental misconception",
    bg: "#fde2e1",
    text: "#b42318",
    dot: "#dc3545",
  },
  RECENTLY_LEARNED: {
    label: "Confusion with recently learned skill",
    bg: "#dbeafe",
    text: "#1d4ed8",
    dot: "#3b82f6",
  },
  LACK_OF_DEPTH: {
    label: "Lack of Depth",
    bg: "#fef3c7",
    text: "#92400e",
    dot: "#f59e0b",
  },
}

// A wrong answer whose misconception was never tagged. It should not occur in
// BMD-served content, so it has no card, but the row still needs a dot.
const UNTAGGED_DOT = "#9ca3af"

const OUTCOME_ORDER: OutcomeKey[] = [
  "correct",
  "FUNDAMENTAL",
  "RECENTLY_LEARNED",
  "LACK_OF_DEPTH",
]

/** Which card a row belongs to. Correct wins over any misconception tag. */
function outcomeOf(row: BmdSkillGapRow): OutcomeKey | null {
  if (row.is_correct) return "correct"
  const m = (row.misconception_type || "").toUpperCase()
  return (OUTCOME_ORDER as string[]).includes(m) ? (m as OutcomeKey) : null
}

/** Card counts for a row set. One definition, used by the cards and the strip. */
function countByOutcome(rows: BmdSkillGapRow[]): Record<OutcomeKey, number> {
  const counts: Record<OutcomeKey, number> = {
    correct: 0,
    FUNDAMENTAL: 0,
    RECENTLY_LEARNED: 0,
    LACK_OF_DEPTH: 0,
  }
  for (const r of rows) {
    const k = outcomeOf(r)
    if (k) counts[k] += 1
  }
  return counts
}

type OutcomeCardsProps = {
  rows: BmdSkillGapRow[]
  active: OutcomeKey | typeof PRIORITY_FILTER_ALL
  onChange: (next: OutcomeKey | typeof PRIORITY_FILTER_ALL) => void
}

const OutcomeCards: React.FC<OutcomeCardsProps> = ({
  rows,
  active,
  onChange,
}) => {
  const counts = React.useMemo(() => countByOutcome(rows), [rows])

  return (
    <div className="d-flex flex-wrap gap-3 mb-3">
      {OUTCOME_ORDER.map((key) => {
        const meta = OUTCOME_META[key]
        const isActive = active === key
        return (
          <button
            key={key}
            type="button"
            className="rounded p-3 flex-fill text-start border-0"
            style={{
              minWidth: 200,
              backgroundColor: meta.bg,
              outline: isActive ? `2px solid ${meta.text}` : "none",
              cursor: "pointer",
            }}
            onClick={() => onChange(isActive ? PRIORITY_FILTER_ALL : key)}
            aria-pressed={isActive}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="fw-bold" style={{ color: meta.text }}>
                  {meta.label} ({counts[key]})
                </div>
              </div>
              <i
                className="fas fa-chevron-right text-muted font-size-12 mt-1"
                aria-hidden="true"
              ></i>
            </div>
          </button>
        )
      })}
    </div>
  )
}

type SkillsGapTableProps = {
  rows: BmdSkillGapRow[]
  totalSkillsInYear: number
}

type SortField = "skill" | "topic" | "year" | "response"
type SortDir = "asc" | "desc"

const FILTER_ALL = "__all__"

const SortHeader: React.FC<{
  field: SortField
  label: string
  width: string
  sortField: SortField | null
  sortDir: SortDir
  onSort: (f: SortField) => void
}> = ({ field, label, width, sortField, sortDir, onSort }) => {
  const isActive = sortField === field
  const iconClass = !isActive
    ? "fas fa-sort"
    : sortDir === "asc"
      ? "fas fa-sort-up"
      : "fas fa-sort-down"
  return (
    <th
      style={{ width, cursor: "pointer", userSelect: "none" }}
      onClick={() => onSort(field)}
      aria-sort={
        !isActive ? "none" : sortDir === "asc" ? "ascending" : "descending"
      }
    >
      <span className="d-inline-flex align-items-center">
        {label}
        <i
          className={`${iconClass} ms-2`}
          style={{
            color: isActive ? "#1f2a44" : "#9ca3af",
            fontSize: 10,
          }}
          aria-hidden="true"
        ></i>
      </span>
    </th>
  )
}

const SkillsGapTable: React.FC<SkillsGapTableProps> = ({
  rows,
  totalSkillsInYear,
}) => {
  const [sortField, setSortField] = React.useState<SortField | null>(null)
  const [sortDir, setSortDir] = React.useState<SortDir>("asc")
  const [topicFilter, setTopicFilter] = React.useState<string>(FILTER_ALL)
  const [yearFilter, setYearFilter] = React.useState<string>(FILTER_ALL)
  const [responseFilter, setResponseFilter] = React.useState<string>(FILTER_ALL)

  const topicOptions = React.useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) if (r.topic_name) set.add(r.topic_name)
    return Array.from(set).sort()
  }, [rows])

  const yearOptions = React.useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) if (r.year_level_name) set.add(r.year_level_name)
    // Sort numerically; rows with no parseable year (null) sort last.
    return Array.from(set).sort(
      (a, b) =>
        (parseYearNumber(a) ?? Number.POSITIVE_INFINITY) -
        (parseYearNumber(b) ?? Number.POSITIVE_INFINITY),
    )
  }, [rows])

  // Drop the column filter back to "all" when its selected value is no longer
  // present, which happens when an outcome card or a year-level change shrinks
  // the input set.
  React.useEffect(() => {
    if (
      topicFilter !== FILTER_ALL &&
      !topicOptions.includes(topicFilter)
    ) {
      setTopicFilter(FILTER_ALL)
    }
  }, [topicOptions, topicFilter])
  React.useEffect(() => {
    if (yearFilter !== FILTER_ALL && !yearOptions.includes(yearFilter)) {
      setYearFilter(FILTER_ALL)
    }
  }, [yearOptions, yearFilter])

  const visible = React.useMemo(() => {
    let out = rows.filter((r) => {
      if (topicFilter !== FILTER_ALL && r.topic_name !== topicFilter)
        return false
      if (yearFilter !== FILTER_ALL && r.year_level_name !== yearFilter)
        return false
      if (responseFilter === "correct" && !r.is_correct) return false
      if (responseFilter === "incorrect" && r.is_correct) return false
      return true
    })
    if (sortField) {
      const dir = sortDir === "asc" ? 1 : -1
      out = [...out].sort((a, b) => {
        switch (sortField) {
          case "skill": {
            const av = plainText(a.skill_name).toLocaleLowerCase()
            const bv = plainText(b.skill_name).toLocaleLowerCase()
            return av.localeCompare(bv) * dir
          }
          case "topic": {
            const av = (a.topic_name || "").toLocaleLowerCase()
            const bv = (b.topic_name || "").toLocaleLowerCase()
            return av.localeCompare(bv) * dir
          }
          case "year": {
            // Rows with no parseable year sort last regardless of direction.
            const av = parseYearNumber(a.year_level_name)
            const bv = parseYearNumber(b.year_level_name)
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            return (av - bv) * dir
          }
          case "response": {
            // asc: incorrect (0) before correct (1).
            const av = a.is_correct ? 1 : 0
            const bv = b.is_correct ? 1 : 0
            return (av - bv) * dir
          }
        }
      })
    }
    return out
  }, [rows, sortField, sortDir, topicFilter, yearFilter, responseFilter])

  const handleSort = (f: SortField) => {
    if (sortField === f) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortField(f)
      setSortDir("asc")
    }
  }

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="fw-semibold font-size-14">
          Skills with Gaps (Skill Breakdown)
        </div>
        <div className="text-muted font-size-12">
          Showing {visible.length} of {totalSkillsInYear} attempted skill
          {totalSkillsInYear === 1 ? "" : "s"}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-skills-topic-filter"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Topic:
          </label>
          <select
            id="bmd-skills-topic-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 160 }}
            value={topicFilter}
            onChange={(e) => setTopicFilter(e.target.value)}
            disabled={topicOptions.length === 0}
          >
            <option value={FILTER_ALL}>All</option>
            {topicOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-skills-year-filter"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Year:
          </label>
          <select
            id="bmd-skills-year-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 110 }}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            disabled={yearOptions.length === 0}
          >
            <option value={FILTER_ALL}>All</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-skills-response-filter"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Response:
          </label>
          <select
            id="bmd-skills-response-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 130 }}
            value={responseFilter}
            onChange={(e) => setResponseFilter(e.target.value)}
          >
            <option value={FILTER_ALL}>All</option>
            <option value="correct">Correct</option>
            <option value="incorrect">Incorrect</option>
          </select>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-muted mb-0">
          {rows.length === 0
            ? "No attempted skills in the current selection."
            : "No skills match these filters."}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr className="text-uppercase text-muted fw-bold font-size-11">
                <SortHeader
                  field="skill"
                  label="Skill"
                  width="40%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  field="topic"
                  label="Topic"
                  width="20%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  field="year"
                  label="Required Year Level"
                  width="15%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  field="response"
                  label="Student Response"
                  width="25%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const outcome = outcomeOf(row)
                const dotColor = outcome
                  ? OUTCOME_META[outcome].dot
                  : UNTAGGED_DOT
                return (
                  <tr key={row.pk_session_question_key}>
                    <td>
                      <div className="d-flex align-items-center">
                        <span
                          className="rounded-circle me-2 d-inline-block"
                          style={{
                            width: 8,
                            height: 8,
                            backgroundColor: dotColor,
                          }}
                          aria-hidden="true"
                        ></span>
                        <span className="fw-semibold">
                          {plainText(row.skill_name) || "Skill (unmapped)"}
                        </span>
                      </div>
                    </td>
                    <td className="font-size-13">
                      {row.topic_name || "—"}
                      {row.sub_topic_name && (
                        <div className="text-muted font-size-12">
                          {row.sub_topic_name}
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "#fde2e1",
                          color: "#b42318",
                        }}
                      >
                        {abbreviateYearLevel(row.year_level_name)}
                      </span>
                    </td>
                    <td>
                      {row.is_correct ? (
                        <span className="d-inline-flex align-items-center">
                          <i
                            className="fas fa-check-circle me-2"
                            style={{ color: "#16a34a" }}
                            aria-hidden="true"
                          ></i>
                          <span
                            className="fw-semibold"
                            style={{ color: "#15803d" }}
                          >
                            Correct
                          </span>
                        </span>
                      ) : (
                        <span className="d-inline-flex align-items-center">
                          <i
                            className="fas fa-times-circle me-2"
                            style={{ color: "#dc3545" }}
                            aria-hidden="true"
                          ></i>
                          <span className="fw-semibold me-2">Incorrect</span>
                          <span className="text-muted font-size-12">
                            {humanMisconception(row.misconception_type)}
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main component
// ============================================================================

export type BmdSkillsGapTabProps = {
  studentKey: string
  yearLevelId: number | undefined
  /** Report scope. null means the whole year level. */
  diagnosticsSessionKey?: string | null
  baseYearLevelName: string | null
  studentName: string
  /**
   * Both KPI objects, not six loose numbers.
   *
   * The strip has to follow the scope selector below it. Passing only the base
   * figures meant switching to Overall changed the table and the cards while
   * the headline above them still counted the base test, which is the exact
   * disagreement this panel keeps being reported for.
   */
  kpi: BmdReportKpi | null
  kpiOverall?: BmdReportKpi | null
}

export const BmdSkillsGapTab: React.FC<BmdSkillsGapTabProps> = ({
  studentKey,
  yearLevelId,
  diagnosticsSessionKey = null,
  baseYearLevelName,
  studentName,
  kpi,
  kpiOverall,
}) => {
  const [data, setData] =
    React.useState<GetStudentSkillsGapResponse | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  // Which test the panel counts. Defaults to Overall to match the KPI strip:
  // once a prerequisite test has been sat, the base test is no longer the
  // whole story, and a tutor who never opens the dropdown was reading a
  // partial picture as the total.
  const [scope, setScope] = React.useState<BmdSkillsGapScope>("overall")
  const [outcomeFilter, setOutcomeFilter] = React.useState<
    OutcomeKey | typeof PRIORITY_FILTER_ALL
  >(PRIORITY_FILTER_ALL)

  React.useEffect(() => {
    if (!studentKey || !yearLevelId) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    getStudentSkillsGap(
      {
        student_key: studentKey,
        year_level_id: yearLevelId,
        diagnostics_session_key: diagnosticsSessionKey,
        scope,
      },
      controller.signal,
    )
      .then((resp) => {
        if (controller.signal.aborted) return
        setData(resp)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load skills gap")
        setLoading(false)
      })
    return () => controller.abort()
  }, [studentKey, yearLevelId, diagnosticsSessionKey, scope])

  const baseYearLabel = abbreviateYearLevel(baseYearLevelName)

  // Falls back to the base figures when the server has not sent an overall
  // set, so an older backend shows the base numbers rather than zeroes.
  const activeKpi = (scope === "overall" && kpiOverall) || kpi
  const topicCount = activeKpi?.topics ?? 0
  const attemptedCount = activeKpi?.questions_attempted ?? 0
  const totalQuestions = activeKpi?.questions ?? 0
  const correctCount = activeKpi?.correct ?? 0
  const accuracyPct = activeKpi?.accuracy_pct ?? 0
  const incorrectCount = activeKpi?.incorrect_total ?? 0

  // Memoized so SkillsGapTable's own sort and filter useMemos keep stable refs
  // instead of busting on every parent render. It has to sit above the early
  // returns to keep hook order stable.
  const allRows = data?.skills_with_gaps ?? []
  const filteredRows = React.useMemo(() => {
    const rows = data?.skills_with_gaps ?? []
    return outcomeFilter === PRIORITY_FILTER_ALL
      ? rows
      : rows.filter((r) => outcomeOf(r) === outcomeFilter)
  }, [data, outcomeFilter])

  if (!yearLevelId) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
        No BMD data yet for this student.
      </div>
    )
  }
  if (loading && !data) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
        Loading skills gap…
      </div>
    )
  }
  if (error) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-danger">
        {error}
      </div>
    )
  }
  if (!data) return null

  return (
    <>
      {/* Which test the panel counts. Sits above the strip because it changes
          every number below it, not just the table. */}
      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        <label
          htmlFor="bmd-skills-gap-scope"
          className="text-muted font-size-12 mb-0"
        >
          Showing:
        </label>
        <select
          id="bmd-skills-gap-scope"
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={scope}
          onChange={(e) => setScope(e.target.value as BmdSkillsGapScope)}
        >
          <option value="base">Base test</option>
          <option value="overall">Overall (with prerequisite test)</option>
        </select>
      </div>
      <SummaryStrip
        studentName={studentName}
        baseYearLabel={baseYearLabel}
        topicCount={topicCount}
        attemptedCount={attemptedCount}
        totalQuestions={totalQuestions}
        correctCount={correctCount}
        accuracyPct={accuracyPct}
        incorrectCount={incorrectCount}
        rows={allRows}
        chainsTriggered={data.priority_counts.chains_triggered}
      />
      <OutcomeCards
        rows={allRows}
        active={outcomeFilter}
        onChange={setOutcomeFilter}
      />
      <SkillsGapTable
        rows={filteredRows}
        totalSkillsInYear={data.total_skills_in_year}
      />
    </>
  )
}

export default BmdSkillsGapTab
